/**
 * Sim content identity (ARCHITECTURE §6 "Build compatibility").
 *
 * `PROTOCOL_VERSION` only covers the wire format. Two peers can speak the same protocol and still run different
 * gameplay numbers (a stale cached bundle, a mid-deploy client, an edited tuning constant): an instant desync that
 * looks like a netcode bug. `SIM_CONTENT_HASH` fingerprints everything data-driven that feeds the sim:
 *
 *   - SIM_STATE_VERSION (bump on any SimState shape/semantics change, i.e. on every sim behaviour change that is
 *     not expressed as data)
 *   - every non-function export of constants.ts, tuning.ts, input.ts, enums.ts and characters.ts, by NAME and value
 *     (module namespace keys are sorted, so the order is stable)
 *   - a numeric probe of dmath (dsin/dcos/datan2 at fixed points), so a runtime that computes them differently gets
 *     a different hash instead of a silent desync
 *
 * It is computed once at module load (pure data walk, ~1 ms). The client sends it in `hello.contentHash` and in the
 * HELLO game packet; the server refuses mismatching clients (`version_mismatch`) and only pairs equal hashes.
 */
import * as characters from './characters';
import * as constants from './constants';
import { datan2, dcos, dsin } from './dmath';
import * as enums from './enums';
import { Fnv1a } from './hash';
import * as input from './input';
import * as tuning from './tuning';

/**
 * Bump when the SimState shape or the sim's code-level semantics change (replays and online compatibility).
 * The sim re-exports it; it lives here so the content hash can include it.
 */
export const SIM_STATE_VERSION = 8;

const hashValue = (h: Fnv1a, v: unknown): void => {
  switch (typeof v) {
    case 'number':
      h.byte(1).num(v);
      return;
    case 'boolean':
      h.byte(2).bool(v);
      return;
    case 'string':
      h.byte(3).str(v);
      return;
    case 'object': {
      if (v === null) {
        h.byte(5);
        return;
      }
      if (Array.isArray(v)) {
        h.byte(6).u32(v.length);
        for (const x of v) hashValue(h, x);
        return;
      }
      h.byte(8);
      for (const k of Object.keys(v).sort()) {
        h.str(k);
        hashValue(h, (v as Record<string, unknown>)[k]);
      }
      return;
    }
    default:
      // functions, undefined: skipped (behaviour is covered by SIM_STATE_VERSION and the dmath probe)
      h.byte(0);
  }
};

const hashModule = (h: Fnv1a, name: string, ns: Record<string, unknown>): void => {
  h.str(name);
  for (const k of Object.keys(ns).sort()) {
    const v = ns[k];
    if (typeof v === 'function') continue;
    h.str(k);
    hashValue(h, v);
  }
};

const computeContentHash = (): number => {
  const h = new Fnv1a();
  h.u32(SIM_STATE_VERSION);
  hashModule(h, 'constants', constants);
  hashModule(h, 'tuning', tuning);
  hashModule(h, 'input', input);
  hashModule(h, 'enums', enums);
  hashModule(h, 'characters', characters);
  for (let i = -16; i <= 16; i++) {
    const x = i * 0.4123;
    h.num(dsin(x))
      .num(dcos(x))
      .num(datan2(x, 1.37 - i * 0.11));
  }
  return h.digest();
};

/** u32 fingerprint of the sim's data + state version. Equal on two peers ⇒ same gameplay numbers. */
export const SIM_CONTENT_HASH: number = computeContentHash();
